import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  CrossClientQueryExecution,
  CrossClientQueryExecutionProgress,
  CrossClientQueryInitializedExecution,
  CrossClientQueryPhases,
} from '../../models/CrossClient'
import { User } from '../../models/User'

interface CrossClientLandingState {
  initLoading: boolean
  evaluateLoading: boolean
  currentExecution: CrossClientQueryInitializedExecution | null
  runningExecutions: CrossClientQueryExecution[]
  runningExecutionProgresses: CrossClientQueryExecutionProgress[]
  runningExecutionsLoading: boolean
  completedExecutions: CrossClientQueryExecution[]
  totalCompletedExecutions: number
  completedExecutionProgresses: CrossClientQueryExecutionProgress[]
  completedExecutionsLoading: boolean
  completedExecutionsLoadingMore: boolean
  pollingEnabled: boolean
  teammates: User[]
  teammatesLoading: boolean
}

const initialState: CrossClientLandingState = {
  initLoading: false,
  evaluateLoading: false,
  currentExecution: null,
  runningExecutions: [],
  runningExecutionProgresses: [],
  runningExecutionsLoading: true,
  completedExecutions: [],
  totalCompletedExecutions: 0,
  completedExecutionProgresses: [],
  completedExecutionsLoading: true,
  completedExecutionsLoadingMore: false,
  pollingEnabled: false,
  teammates: [],
  teammatesLoading: true,
}

export const crossClientLandingSlice = createSlice({
  name: 'crossClientLanding',
  initialState,
  reducers: {
    setCrossClientInitLoading: (state, action: PayloadAction<boolean>) => {
      state.initLoading = action.payload
    },
    setCrossClientEvaluateLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluateLoading = action.payload
    },

    setCrossClientLandingExecution: (state, action: PayloadAction<CrossClientQueryInitializedExecution | null>) => {
      state.currentExecution = action.payload
    },
    addCrossClientLandingCompletedExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      const newExecutions = action.payload.filter(
        (e) =>
          !state.completedExecutions.some((completedExecution) => completedExecution.id === e.id) &&
          !state.runningExecutions.some((runningExecution) => runningExecution.id === e.id),
      )
      state.completedExecutions.push(...newExecutions)
    },
    addCrossClientLandingCompletedExecutionProgresses: (state, action: PayloadAction<CrossClientQueryExecutionProgress[]>) => {
      state.completedExecutionProgresses.push(...action.payload)
    },
    setCrossClientLandingCompletedExecutionsLoading: (state, action: PayloadAction<boolean>) => {
      state.completedExecutionsLoading = action.payload
    },
    setCrossClientLandingCompletedExecutionsLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.completedExecutionsLoadingMore = action.payload
    },
    setCrossClientLandingTotalCompletedExecutions: (state, action: PayloadAction<number>) => {
      state.totalCompletedExecutions = action.payload
    },
    setCrossClientLandingRunningExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      state.runningExecutions = action.payload
    },
    setCrossClientLandingRunningExecutionProgresses: (state, action: PayloadAction<CrossClientQueryExecutionProgress[]>) => {
      state.runningExecutionProgresses = action.payload
      const finishedProgresses = action.payload.filter((e) => e.phase !== CrossClientQueryPhases.PROCESSING)
      for (const progress of finishedProgresses) {
        const runningExecution = state.runningExecutions.find((e) => e.id === progress.executionId)
        if (runningExecution) {
          runningExecution.phase = progress.phase
          runningExecution.updatedAt = progress.lastUpdatedAt
        }
      }
    },
    setCrossClientLandingRunningExecutionsLoading: (state, action: PayloadAction<boolean>) => {
      state.runningExecutionsLoading = action.payload
    },

    startCrossClientLandingPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientLandingPolling: (state) => {
      state.pollingEnabled = false
    },
    setCrossClientLandingTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    setCrossClientLandingTeammatesLoading: (state, action: PayloadAction<boolean>) => {
      state.teammatesLoading = action.payload
    },
    resetCrossClientLanding: () => initialState,
  },
})

export const {
  setCrossClientInitLoading,
  setCrossClientEvaluateLoading,
  addCrossClientLandingCompletedExecutions,
  addCrossClientLandingCompletedExecutionProgresses,
  setCrossClientLandingExecution,
  startCrossClientLandingPolling,
  stopCrossClientLandingPolling,
  setCrossClientLandingRunningExecutions,
  setCrossClientLandingRunningExecutionProgresses,
  setCrossClientLandingRunningExecutionsLoading,
  setCrossClientLandingCompletedExecutionsLoading,
  setCrossClientLandingCompletedExecutionsLoadingMore,
  setCrossClientLandingTotalCompletedExecutions,
  setCrossClientLandingTeammates,
  setCrossClientLandingTeammatesLoading,
  resetCrossClientLanding,
} = crossClientLandingSlice.actions

export const selectCrossClientInitLoading = (state: RootState) => state.crossClientLanding.initLoading

export const selectCrossClientEvaluateLoading = (state: RootState) => state.crossClientLanding.evaluateLoading

export const selectCrossClientLandingCurrentExecution = (state: RootState) => state.crossClientLanding.currentExecution

export const selectCrossClientLandingPollingEnabled = (state: RootState) => state.crossClientLanding.pollingEnabled

export const selectCrossClientLandingCompletedExecutions = (state: RootState) => state.crossClientLanding.completedExecutions

export const selectCrossClientLandingCompletedExecutionProgresses = (state: RootState) => state.crossClientLanding.completedExecutionProgresses

export const selectCrossClientLandingRunningExecutions = (state: RootState) => state.crossClientLanding.runningExecutions

export const selectCrossClientLandingRunningExecutionProgresses = (state: RootState) => state.crossClientLanding.runningExecutionProgresses

export const selectCrossClientLandingCompletedExecutionsLoading = (state: RootState) => state.crossClientLanding.completedExecutionsLoading

export const selectCrossClientLandingCompletedExecutionsLoadingMore = (state: RootState) => state.crossClientLanding.completedExecutionsLoadingMore

export const selectCrossClientLandingRunningExecutionsLoading = (state: RootState) => state.crossClientLanding.runningExecutionsLoading

export const selectCrossClientLandingTotalCompletedExecutions = (state: RootState) => state.crossClientLanding.totalCompletedExecutions

export const selectCrossClientLandingRunningExecutionCount = (state: RootState) => {
  return state.crossClientLanding.runningExecutions.filter((re) => re.phase === CrossClientQueryPhases.PROCESSING).length
}

export const selectCrossClientLandingTeammates = (state: RootState) => state.crossClientLanding.teammates

export const selectCrossClientLandingTeammatesLoading = (state: RootState) => state.crossClientLanding.teammatesLoading

export default crossClientLandingSlice.reducer
